import { Component, createContext } from "react";
import { mainClient } from "../network/mainClient";
import { authStorage } from "./authStorage";
import { getSortAscending } from "../modules/verificationRequest/utils/getSort";
import { AUTH_COMPANY_ID, TCompany } from "../modules/company/models";
import { userLogout } from "../network/mainClient/auth";
import { IUserCompanyRoleObject } from "../modules/verificationRequest/network";

export enum CompanyRole {
  VIEWER = "viewer",
  ADMIN = "admin",
  MANAGER = "account_manager",
  FINAL_REVIEWER = "final_reviewer",
}
export type UatLoginType = "compliance" | "superAdmin";

export type AuthState = {
  loading: boolean;
  loaded: boolean;
  authenticated: boolean;
  companyRoleObj: IUserCompanyRoleObject;
  companyRole: CompanyRole;
  isSuperAdmin: boolean;
  authorizedCompanyId: string | null;
  authorizedUserEmail: string | null;
  companies: { items: TCompany[] } | null;
};

export type AuthContextValue = AuthState & {
  requestSms: typeof mainClient.auth.requestSms;
  authWithCode: typeof mainClient.auth.authWithCode;
  authSuperAdmin: typeof mainClient.auth.authSuperAdmin;
  startLogin: typeof mainClient.auth.startLogin;
  verifyFactor: typeof mainClient.auth.verifyFactor;
  resendFactor: typeof mainClient.auth.resendFactor;
  getOperationsFactor: typeof mainClient.auth.getOperationsFactor;
  sendOperationsFactor: typeof mainClient.auth.sendOperationsFactor;
  changePassword: typeof mainClient.auth.changePassword;
  setAuthorizedCompanyId: (companyId: string) => void;
  setAuthorizedUserEmail: (string: string) => void;
  logout: () => Promise<boolean>;
  setCompanies: (companies: { items: TCompany[] } | null) => void;
  setCompanyRoleObj: (roleObj: IUserCompanyRoleObject) => void;
  setCompanyRole: (role: CompanyRole) => void;
  setIsSuperAdmin: (isSuperAdmin: boolean) => void;
};

export const AuthContext = createContext<AuthContextValue>(
  {} as AuthContextValue
);

export class AuthProvider extends Component<{}, AuthState> {
  state = {
    loading: true,
    loaded: false,
    authenticated: false,
    authorizedCompanyId: null,
    authorizedUserEmail: null,
    companies: null,
    companyRoleObj: {} as IUserCompanyRoleObject,
    companyRole: CompanyRole.VIEWER,
    isSuperAdmin: false,
  };

  componentDidMount() {
    this.restore();
  }

  setCompanyRoleObj = (companyRoleObj: IUserCompanyRoleObject) => {
    this.setState({
      companyRoleObj,
    });
  };
  setCompanyRole = (companyRole: CompanyRole) => {
    this.setState({
      companyRole,
    });
  };

  setAuthorizedUserEmail = (authorizedUserEmail: string) => {
    this.setState({
      authorizedUserEmail,
    });
  };

  setIsSuperAdmin = (isSuperAdmin: boolean) => {
    this.setState({
      isSuperAdmin,
    });
  };

  restore = async () => {
    const data = await authStorage.getAuthData();
    const authorizedCompanyId = localStorage.getItem(AUTH_COMPANY_ID) as string;
    this.setState({
      loading: false,
      loaded: true,
      authenticated: data !== null,
      authorizedCompanyId,
      companies: null,
    });
  };

  requestSms = async (phone: string) => {
    return await mainClient.auth.requestSms(phone);
  };

  authWithCode = async (phone: string, code: string, salt: string) => {
    const result = await mainClient.auth.authWithCode(phone, code, salt);
    authStorage.setAuthData(result);
    this.setState({
      authenticated: true,
    });
    return result;
  };

  authSuperAdmin = async (email: string, password: string) => {
    const result = await mainClient.auth.authSuperAdmin(email, password);
    this.setState({
      isSuperAdmin: result.isSuperAdmin,
    });
    authStorage.setAuthData(result.token);
    this.setState({
      authenticated: true,
    });
    return result;
  };

  startLogin = async (
    flow: "LOGIN_FLOW_COCKPIT" | "LOGIN_FLOW_USER_APP",
    identity: string,
    verificationRequestId?: number,
    verificationRequestToken?: string
  ) => {
    return await mainClient.auth.startLogin(
      flow,
      identity,
      verificationRequestId,
      verificationRequestToken
    );
  };

  verifyFactor = async (
    authSessionId: string,
    identity: string,
    factorId: string,
    code: string
  ) => {
    const result = await mainClient.auth.verifyFactor(
      authSessionId,
      identity,
      factorId,
      code
    );
    if (result.session) {
      authStorage.setAuthData({
        ...result.session,
        expired: "",
      });
      this.setState({
        authenticated: true,
      });
    }
    return result;
  };

  resendFactor = async (
    authSessionId: string,
    identity: string,
    factorId: string
  ) => {
    return await mainClient.auth.resendFactor(
      authSessionId,
      identity,
      factorId
    );
  };

  getOperationsFactor = async () => {
    return await mainClient.auth.getOperationsFactor();
  };

  sendOperationsFactor = async () => {
    return await mainClient.auth.sendOperationsFactor();
  };

  changePassword = async (
    operationsSessionId: string,
    factorId: string,
    code: string,
    newPassword: string
  ) => {
    return await mainClient.auth.changePassword(
      operationsSessionId,
      factorId,
      code,
      newPassword
    );
  };

  logout = async () => {
    userLogout();
    authStorage.clear();
    this.setState({
      authenticated: false,
      authorizedCompanyId: null,
    });
    return true;
  };

  // setTokenData = (tokenData: TokenData) => {
  //   if (!tokenData) {
  //     throw new Error("Token Data not provided");
  //   }
  //   authStorage.setAuthData(tokenData);
  //   this.setState({
  //     authorizedCompanyId: tokenData.companyId || null,
  //   });
  // };

  setAuthorizedCompanyId = (companyId: string) => {
    this.setState({
      authorizedCompanyId: companyId || null,
    });
  };

  setCompanies = (data: any | null) => {
    this.setState({
      companies: { items: getSortAscending(data.items, "name") },
    });
  };

  render() {
    const { children } = this.props;

    const contextValue: AuthContextValue = {
      ...this.state,
      requestSms: this.requestSms,
      setCompanyRole: this.setCompanyRole,
      setCompanyRoleObj: this.setCompanyRoleObj,
      setAuthorizedUserEmail: this.setAuthorizedUserEmail,
      setIsSuperAdmin: this.setIsSuperAdmin,
      authWithCode: this.authWithCode,
      authSuperAdmin: this.authSuperAdmin,
      startLogin: this.startLogin,
      verifyFactor: this.verifyFactor,
      resendFactor: this.resendFactor,
      getOperationsFactor: this.getOperationsFactor,
      sendOperationsFactor: this.sendOperationsFactor,
      changePassword: this.changePassword,
      setAuthorizedCompanyId: this.setAuthorizedCompanyId,
      setCompanies: this.setCompanies,
      logout: this.logout,
    };

    return (
      <AuthContext.Provider value={contextValue}>
        {children}
      </AuthContext.Provider>
    );
  }
}
